import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Typography, Grid, MenuItem, TextField, Skeleton, Stack, Chip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MapLocationPicker from 'views/common/MapLocationPicker';
import vendorService from 'api/vendorService';
import statesAndLgas from 'utils/statesAndLgas';
import { toast, ToastContainer } from 'react-toastify';
import { gridSpacing } from 'store/constant';

const VendorLocation = ({ data, isLoading }) => {
    const [state, setState] = useState('');
    const [lga, setLga] = useState('');
    const [buildingNumber, setBuildingNumber] = useState('');
    const [street, setStreet] = useState('');
    const [zipcode, setZipcode] = useState('');
    const [label, setLabel] = useState('Business');
    const [formattedAddress, setFormattedAddress] = useState('');
    const [coordinates, setCoordinates] = useState(null);
    const [loading, setLoading] = useState(false);

    // Initialize data from API
    useEffect(() => {
        if (data?.vendor) {
            const loc = data.vendor.location || {};
            setFormattedAddress(loc.formattedAddress || data.vendor.address || '');
            setState(loc.state || data.vendor.state || '');
            setLga(loc.city || data.vendor.lga || '');
            setBuildingNumber(loc.buildingNumber || '');
            setStreet(loc.street || '');
            setZipcode(loc.zipcode || '');
            setLabel(loc.label || 'Business');

            if (loc?.coordinates) {
                // MongoDB stores coordinates as [longitude, latitude]
                const [lng, lat] = loc.coordinates;
                setCoordinates({ lat, lng });
            }
        }
    }, [data]);

    // ✅ Extract State and LGA from search selection
    const handleAddressSelect = (locationData) => {
        if (!locationData) return;

        setCoordinates({ lat: locationData.lat, lng: locationData.lng });
        setFormattedAddress(locationData.display_name || '');
        setStreet(locationData.street || '');
        setBuildingNumber(locationData.buildingNumber || '');
        setZipcode(locationData.zipcode || '');

        // Normalize State
        if (locationData.state) {
            const normalizedState = locationData.state.replace(/\sState$/i, '');
            // Only set if it exists in our list
            if (statesAndLgas[normalizedState]) {
                setState(normalizedState);
            } else {
                setState(normalizedState); // fallback to raw state if not strictly matching
            }
        }

        // Normalize LGA/City
        if (locationData.city) {
            const normalizedCity = locationData.city.replace(/\b(Local Government Area|LGA|Local Government)\b/gi, '').trim();
            setLga(normalizedCity);
        }
    };

    const handleSave = async () => {
        if (!coordinates) return toast.error('Please select a location on the map');

        try {
            setLoading(true);
            // Sync with Legacy Fields for backward compatibility
            await vendorService.updateVendor({ address: formattedAddress, lga, state });

            // Rich Location Update
            await vendorService.updateVendorCoordinate({
                coordinates: [coordinates.lat, coordinates.lng],
                formattedAddress,
                buildingNumber,
                street,
                city: lga,
                state,
                zipcode,
                label
            });

            toast.success('Location settings updated!');
        } catch (err) {
            toast.error('Failed to save location');
        } finally {
            setLoading(false);
        }
    };

    if (isLoading) return <Skeleton variant="rectangular" height={500} sx={{ borderRadius: 3 }} />;

    const isVerified = data?.vendor?.kycStatus === 'verified' || data?.vendor?.kycStatus === 'approved';

    return (
        <Box container spacing={gridSpacing} paddingX={5} pt={3}>
            <ToastContainer />
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6" fontWeight={600}>
                    Store Location Details
                </Typography>
                {isVerified && (
                    <Chip
                        label="VERIFIED"
                        color="success"
                        size="small"
                        icon={<CheckCircleIcon sx={{ fontSize: '1rem !important' }} />}
                        sx={{ fontWeight: 700 }}
                    />
                )}
            </Stack>

            {/* The Map Utility Component */}
            <MapLocationPicker
                initialLat={coordinates?.lat}
                initialLng={coordinates?.lng}
                onChange={(lat, lng) => setCoordinates({ lat, lng })}
                onAddressSelect={handleAddressSelect}
                isLoading={loading}
            />

            <Grid container spacing={2} mt={2}>
                <Grid item xs={12} md={4}>
                    <TextField
                        fullWidth
                        label="Building / Suite Number"
                        value={buildingNumber}
                        onChange={(e) => setBuildingNumber(e.target.value)}
                    />
                </Grid>
                <Grid item xs={12} md={8}>
                    <TextField
                        fullWidth
                        label="Street Name"
                        value={street}
                        onChange={(e) => setStreet(e.target.value)}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        select
                        label="State"
                        value={state}
                        onChange={(e) => {
                            setState(e.target.value);
                            setLga(''); // Reset LGA when state changes
                        }}
                    >
                        {Object.keys(statesAndLgas)
                            .sort()
                            .map((s) => (
                                <MenuItem key={s} value={s}>
                                    {s}
                                </MenuItem>
                            ))}
                    </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        select
                        label="LGA / City"
                        value={lga}
                        onChange={(e) => setLga(e.target.value)}
                        disabled={!state}
                        helperText="Ensure this matches your physical district"
                    >
                        {(statesAndLgas[state] || []).map((l) => (
                            <MenuItem key={l} value={l}>
                                {l}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="Postcode / Zip"
                        value={zipcode}
                        onChange={(e) => setZipcode(e.target.value)}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="Address Label (e.g. Head Office)"
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Button variant="contained" size="large" disabled={loading} onClick={handleSave} sx={{ mt: 2, px: 5 }}>
                        {loading ? 'Saving...' : 'Confirm Location'}
                    </Button>
                </Grid>
            </Grid>
        </Box>
    );
};

VendorLocation.propTypes = {
    data: PropTypes.shape({
        vendor: PropTypes.shape({
            location: PropTypes.object,
            address: PropTypes.string,
            state: PropTypes.string,
            lga: PropTypes.string,
            kycStatus: PropTypes.string
        })
    }),
    isLoading: PropTypes.bool
};

export default VendorLocation;
